import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, FileText, Mail, Image, Users, Settings, LogOut, BookOpen, Palette, Menu, LayoutTemplate, UserCog, ClipboardList, Newspaper, Globe, Building2, Sparkles, DollarSign, Shield, Scale, HelpCircle, UserX, ScrollText, ExternalLink, BarChart3, Handshake, Lock, Crown, Rocket, Store, Bot, Smartphone, CalendarDays, Megaphone, Key, ChevronDown, ChevronRight, CreditCard, Activity } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import api from '../utils/api';
import SimpleTooltip from './SimpleTooltip';
import TenantSwitcher from './TenantSwitcher';
import AiGenerationsPanel from './AiGenerationsPanel';
import { usePlanLimits } from '../hooks/usePlanLimits';

// Menu items accessible on the Freemium plan. Everything else is greyed out
// with an "Upgrade" tooltip so freemium users see the full scope of features
// but can only click into what their plan supports.
const FREEMIUM_ACCESSIBLE_PATHS = new Set([
  '/dashboard',
  '/landing-page',
  '/navigation',      // Required for the Landing Page's nav menus
  '/forms',
  '/media',
  '/help',
  '/settings',
  '/affiliate/dashboard',
  '/affiliate/apply',
  '/marketplace',
]);

// Admin section items accessible on the Freemium plan. Freemium customers
// use a subdomain (e.g., school.eu.nexuscms.ai), not a custom domain — but
// they still need this page to pick/configure their subdomain.
const FREEMIUM_ACCESSIBLE_ADMIN_PATHS = new Set([
  '/domain-settings',
]);

const Sidebar = () => {
  const location = useLocation();
  const { user, logout, isImpersonating, stopImpersonation } = useAuth();
  const { branding } = useBranding();
  const [aiCredits, setAiCredits] = useState(null);
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [affiliateJoined, setAffiliateJoined] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  // Admin section visibility — based on the (impersonated) user's actual role.
  // When a super admin impersonates a tenant admin, the Admin section must
  // remain visible so they can manage Custom Domain / Compliance / Organization
  // Access on the tenant's behalf.
  const isAdminOrSuper = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'master_admin';
  // Super Admin section stays gated — impersonated users (regardless of the
  // impersonator's elevated role) never see platform-wide super admin tools.
  const isSuperAdmin = !isImpersonating && (user?.role === 'super_admin' || user?.role === 'master_admin');
  const isMasterAdmin = !isImpersonating && user?.role === 'master_admin';
  const isResellerAdmin = user?.reseller_id && user?.reseller_role === 'admin';
  const isReseller = user?.reseller_id;

  // Plan gating — Freemium gets a restricted, "look but don't touch" sidebar.
  // Source of truth = tenant.plan_level (via /tenant/usage). The cached
  // `user.plan` on the user document can drift, so prefer the live value.
  const { planLevel: livePlanLevel } = usePlanLimits();
  const planLevel = (livePlanLevel || user?.plan || 'freemium').toLowerCase();
  const isFreemium = planLevel === 'freemium' || planLevel === 'free';

  // Dynamic colors from branding
  const primaryColor = branding.primary_color || '#4F46E5';
  const companyName = branding.company_name || 'Nexus CMS';

  // Fetch AI credits on mount and periodically
  useEffect(() => {
    const fetchCredits = async () => {
      try {
        const res = await api.get('/ai/credits');
        setAiCredits(res.data);
      } catch (error) {
        console.error('Failed to fetch AI credits:', error);
      }
    };
    
    if (user && !isReseller) {
      fetchCredits();
      // Refresh credits every 30 seconds
      const interval = setInterval(fetchCredits, 30000);
      return () => clearInterval(interval);
    }
  }, [user, isReseller]);

  // Fetch affiliate status once so the menu label can read "Become an Affiliate"
  // for users who haven't joined yet, and "Affiliate" once they have.
  useEffect(() => {
    if (!user || isReseller) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/affiliate/status');
        if (!cancelled) setAffiliateJoined(!!res.data?.is_affiliate);
      } catch (e) {
        if (!cancelled) setAffiliateJoined(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, isReseller]);

  // Reseller-specific menu (when logged in as reseller)
  if (isReseller) {
    // Reseller business console
    const resellerSection = [
      { name: 'Dashboard', path: '/reseller/dashboard', icon: LayoutDashboard },
      { name: 'Customers', path: '/reseller/dashboard?tab=customers', icon: Users },
      { name: 'Billing', path: '/reseller/billing', icon: DollarSign },
    ];
    // Full CMS on the reseller's own demo workspace — so they can build and
    // demonstrate exactly what a customer gets ("see everything").
    const workspaceSection = [
      { name: 'Site Overview', path: '/dashboard', icon: LayoutDashboard },
      { name: 'Pages', path: '/pages', icon: FileText },
      { name: 'Articles', path: '/articles', icon: Newspaper },
      { name: 'Newsletters', path: '/newsletters', icon: Mail },
      { name: 'Navigation', path: '/navigation', icon: Menu },
      { name: 'Forms', path: '/forms', icon: ClipboardList },
      { name: 'Events', path: '/events', icon: CalendarDays },
      { name: 'Popups', path: '/popups', icon: Megaphone },
      { name: 'Media Library', path: '/media', icon: Image },
      { name: 'Analytics', path: '/analytics', icon: BarChart3 },
      { name: 'AI Assistant', path: '/ai-assistant', icon: Bot },
      { name: 'Taptile', path: '/taptile', icon: Smartphone },
      { name: 'Landing Page', path: '/landing-page', icon: Globe },
      { name: 'Team', path: '/team', icon: Users },
      { name: 'Site Theme', path: '/theme-settings', icon: Palette },
      { name: 'Templates', path: '/templates', icon: LayoutTemplate },
      { name: 'Redirects', path: '/redirects', icon: ExternalLink },
      { name: 'Custom Domain', path: '/domain-settings', icon: Globe, roleColor: 'admin' },
      { name: 'Compliance', path: '/compliance', icon: Scale, roleColor: 'admin' },
      { name: 'Security', path: '/security-settings', icon: Shield },
      { name: 'API Keys', path: '/api-keys', icon: Key },
      { name: 'Help & Tutorials', path: '/help', icon: HelpCircle },
      { name: 'Settings', path: '/settings', icon: Settings },
    ];

    // Active-state: match full path + query so Dashboard and Customers (which
    // share the /reseller/dashboard route) never both highlight at once.
    const currentLoc = location.pathname + (location.search || '');
    const isResellerItemActive = (itemPath) => {
      if (itemPath === currentLoc) return true;
      if (itemPath === '/reseller/dashboard' && location.pathname === '/reseller/dashboard' && !location.search) return true;
      if (itemPath !== '/reseller/dashboard' && !itemPath.includes('?') && location.pathname === itemPath) return true;
      return false;
    };

    const resellerLinkColor = (item, active) => {
      if (active) return 'text-white';
      if (item.roleColor === 'admin') return 'text-red-400 hover:text-red-300 hover:bg-red-900/30';
      return 'text-slate-300 hover:bg-slate-800 hover:text-white';
    };

    const renderResellerLink = (item) => {
      const Icon = item.icon;
      const active = isResellerItemActive(item.path);
      return (
        <Link
          key={item.path + item.name}
          to={item.path}
          data-testid={`sidebar-link-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
          className={`flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-all ${resellerLinkColor(item, active)}`}
          style={active ? { backgroundColor: primaryColor } : {}}
        >
          <Icon size={20} />
          <span>{item.name}</span>
        </Link>
      );
    };

    return (
      <div className="w-64 bg-slate-900 min-h-screen sticky top-0 text-white flex flex-col relative" data-testid="sidebar">
        {/* Impersonation Banner (reseller portal) */}
        {isImpersonating && (() => {
          const impRole = user?.impersonator_role;
          const isMasterImpersonator = impRole === 'master_admin';
          const bannerClass = isMasterImpersonator ? 'bg-rose-500 text-rose-950' : 'bg-amber-500 text-amber-900';
          const stopBtnClass = isMasterImpersonator ? 'bg-rose-600 hover:bg-rose-700' : 'bg-amber-600 hover:bg-amber-700';
          const RoleIcon = isMasterImpersonator ? Crown : UserX;
          const roleLabel = isMasterImpersonator ? 'Master Admin' : 'Super Admin';
          return (
            <div className={`${bannerClass} px-4 py-2 text-sm`} data-testid="impersonation-banner">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <RoleIcon size={16} />
                  <span className="font-medium">Impersonating</span>
                </div>
                <button onClick={stopImpersonation} className={`px-2 py-1 ${stopBtnClass} text-white text-xs rounded font-medium transition-colors`} data-testid="stop-impersonation-btn">
                  Stop
                </button>
              </div>
              <p className="text-xs mt-1 opacity-90">{user?.email}</p>
              {user?.impersonator_email && (
                <p className="text-[11px] mt-0.5 opacity-90 flex items-center gap-1" data-testid="impersonator-role-badge">
                  <RoleIcon size={11} />
                  <span>via {roleLabel}: {user.impersonator_email}</span>
                </p>
              )}
            </div>
          );
        })()}

        <div className="p-6">
          {branding.logo_url ? (
            <img
              src={branding.logo_url}
              alt={companyName}
              className="h-8 object-contain mb-2"
            />
          ) : (
            <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {companyName && companyName !== 'Nexus CMS' ? companyName : 'Reseller Portal'}
            </h1>
          )}
          <p className="text-sm text-slate-400 mt-1">{user?.email}</p>
        </div>

        <nav className="flex-1 px-4 overflow-y-auto pb-20">
          <p className="px-4 text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Reseller</p>
          {resellerSection.map(renderResellerLink)}
          <p className="px-4 mt-5 text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Demo Workspace</p>
          {workspaceSection.map(renderResellerLink)}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-800 bg-slate-900">
          <button
            onClick={logout}
            data-testid="logout-button"
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-slate-800 hover:text-white transition-all w-full"
          >
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </div>
      </div>
    );
  }

  // Regular CMS menu - Main items visible to all users
  const affiliatePath = affiliateJoined ? '/affiliate/dashboard' : '/affiliate/apply';
  const affiliateLabel = affiliateJoined ? 'Affiliate' : 'Become an Affiliate';

  // ── Grouped menu structure ──────────────────────────────────────────────────
  const topLevelItems = [
    { name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard, tooltip: 'View your overview and stats' },
    { name: 'Events',    path: '/events',    icon: CalendarDays,    tooltip: 'Create events, calendars and audience groups' },
  ];

  const menuGroups = [
    {
      label: 'Content',
      items: [
        { name: 'Pages',         path: '/pages',        icon: FileText,      tooltip: 'Create and manage web pages' },
        { name: 'Articles',      path: '/articles',     icon: Newspaper,     tooltip: 'Write and publish articles' },
        { name: 'Newsletters',   path: '/newsletters',  icon: Mail,          tooltip: 'Create and send newsletters' },
        { name: 'Forms',         path: '/forms',        icon: ClipboardList, tooltip: 'Build and manage forms' },
        { name: 'Popups',        path: '/popups',       icon: Megaphone,     tooltip: 'Show announcements or forms when visitors open a page' },
        { name: 'Media Library', path: '/media',        icon: Image,         tooltip: 'Upload and manage files' },
      ],
    },
    {
      label: 'Design',
      items: [
        { name: 'Navigation',   path: '/navigation',     icon: Menu,           tooltip: 'Configure site navigation' },
        { name: 'Landing Page', path: '/landing-page',   icon: Globe,          tooltip: 'Edit your site landing page' },
        { name: 'Site Theme',   path: '/theme-settings', icon: Palette,        tooltip: 'Customize colors and branding' },
        { name: 'Templates',    path: '/templates',      icon: LayoutTemplate, tooltip: 'Manage page templates' },
      ],
    },
    {
      label: 'Grow',
      items: [
        { name: 'Analytics',    path: '/analytics',    icon: BarChart3, tooltip: 'Track performance and growth' },
        { name: 'Marketplace',  path: '/marketplace',  icon: Store,     tooltip: 'Discover trusted partners and tools' },
        { name: affiliateLabel, path: affiliatePath,   icon: Handshake, tooltip: affiliateJoined ? 'Your affiliate dashboard and referral link' : 'Earn commissions by referring customers' },
      ],
    },
    {
      label: 'Integrations',
      items: [
        { name: 'AI Chatbots',      path: '/ai-assistant',    icon: Bot,        tooltip: 'Set up your website AI chatbot (Pro & above)' },
        { name: 'Taptile',          path: '/taptile',         icon: Smartphone, tooltip: 'Manage your Taptile app inside Nexus' },
        { name: 'Stripe Payments',  path: '/stripe-payments', icon: CreditCard, tooltip: 'Connect Stripe to receive event ticket payouts directly' },
      ],
    },
    {
      label: 'Advanced',
      items: [
        { name: 'Security',  path: '/security-settings', icon: Shield,       tooltip: 'Manage 2FA and view audit logs' },
        { name: 'API Keys',  path: '/api-keys',          icon: Key,          tooltip: 'Generate API keys for headless integrations' },
        { name: 'API Docs',  path: '/api-docs',          icon: BookOpen,     tooltip: 'API documentation for developers' },
        { name: 'Redirects', path: '/redirects',         icon: ExternalLink, tooltip: 'Manage URL redirects' },
      ],
    },
    {
      label: 'Account',
      items: [
        { name: 'Team',            path: '/team',     icon: Users,      tooltip: 'Manage team members' },
        { name: 'Settings',        path: '/settings', icon: Settings,   tooltip: 'Account settings and billing' },
        { name: 'Help & Tutorials',path: '/help',     icon: HelpCircle, tooltip: 'Get help and learn the platform' },
      ],
    },
  ];

  // Auto-expand the group containing the active page
  useEffect(() => {
    const activeGroup = menuGroups.find(g => g.items.some(i => i.path === location.pathname));
    if (activeGroup) setExpandedGroups(prev => new Set([...prev, activeGroup.label]));
  }, [location.pathname]); 

  const toggleGroup = (label) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label); else next.add(label);
      return next;
    });
  };

  // Admin & Super Admin items (Red color)
  const adminMenuItems = isAdminOrSuper ? [
    { name: 'Organization Access', path: '/organization-members', icon: Building2, tooltip: 'Manage who has access to this organization', roleColor: 'admin' },
    { name: 'Custom Domain', path: '/domain-settings', icon: Globe, tooltip: 'Configure your domain', roleColor: 'admin' },
    { name: 'Compliance', path: '/compliance', icon: Scale, tooltip: 'View compliance documents', roleColor: 'admin' },
  ] : [];

  // Super Admin only items (Gold color)
  const superAdminMenuItems = (isSuperAdmin && !isReseller) ? [
    { name: 'User Management', path: '/users', icon: UserCog, tooltip: 'Manage all platform users', roleColor: 'super_admin' },
    { name: 'Audit Logs', path: '/audit-logs', icon: ScrollText, tooltip: 'View tenant activity logs', roleColor: 'super_admin' },
    { name: 'Webhook Logs', path: '/webhook-logs', icon: Activity, tooltip: 'Stripe webhook event log', roleColor: 'super_admin' },
    { name: 'Resellers', path: '/resellers', icon: Building2, tooltip: 'Manage reseller accounts', roleColor: 'super_admin' },
    { name: 'Affiliates', path: '/affiliates', icon: Handshake, tooltip: 'Manage affiliate program', roleColor: 'super_admin' },
    { name: 'Go Live Requests', path: '/go-live-requests', icon: Rocket, tooltip: 'Review tenants ready to launch', roleColor: 'super_admin' },
  ] : [];

  // Master Admin only items (Crimson / Crown color) — platform-owner tools.
  const masterAdminMenuItems = (isMasterAdmin && !isReseller) ? [
    { name: 'CMS Landing', path: '/cms/landing-page', icon: Crown, tooltip: 'Edit the main CMS marketing landing page', roleColor: 'master_admin' },
    { name: 'Plan Limits', path: '/plan-limits', icon: Shield, tooltip: 'Configure per-plan usage caps & feature access', roleColor: 'master_admin' },
    { name: 'AI Bots', path: '/admin/ai-bots', icon: Bot, tooltip: 'Fleet view of every tenant AI bot — usage & cost', roleColor: 'master_admin' },
  ] : [];

  // Helper function to get link color based on role
  const getLinkColor = (item, isActive) => {
    if (isActive) return 'text-white';
    if (item.roleColor === 'admin') return 'text-red-400 hover:text-red-300';
    if (item.roleColor === 'super_admin') return 'text-amber-400 hover:text-amber-300';
    if (item.roleColor === 'master_admin') return 'text-rose-400 hover:text-rose-300';
    return 'text-slate-300 hover:text-white';
  };

  const getHoverBg = (item) => {
    if (item.roleColor === 'admin') return 'hover:bg-red-900/30';
    if (item.roleColor === 'super_admin') return 'hover:bg-amber-900/30';
    if (item.roleColor === 'master_admin') return 'hover:bg-rose-900/30';
    return 'hover:bg-slate-800';
  };

  const isActive = (path) => location.pathname === path;

  return (
    <div className="w-64 bg-slate-900 min-h-screen sticky top-0 text-white flex flex-col relative" data-testid="sidebar">
      {/* Impersonation Banner */}
      {isImpersonating && (() => {
        const impRole = user?.impersonator_role;
        const isMasterImpersonator = impRole === 'master_admin';
        const bannerClass = isMasterImpersonator
          ? 'bg-rose-500 text-rose-950'
          : 'bg-amber-500 text-amber-900';
        const stopBtnClass = isMasterImpersonator
          ? 'bg-rose-600 hover:bg-rose-700'
          : 'bg-amber-600 hover:bg-amber-700';
        const RoleIcon = isMasterImpersonator ? Crown : UserX;
        const roleLabel = isMasterImpersonator ? 'Master Admin' : 'Super Admin';
        return (
          <div className={`${bannerClass} px-4 py-2 text-sm`} data-testid="impersonation-banner">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <RoleIcon size={16} />
                <span className="font-medium">Impersonating</span>
              </div>
              <button
                onClick={stopImpersonation}
                className={`px-2 py-1 ${stopBtnClass} text-white text-xs rounded font-medium transition-colors`}
                data-testid="stop-impersonation-btn"
              >
                Stop
              </button>
            </div>
            <p className="text-xs mt-1 opacity-90">{user?.email}</p>
            {user?.impersonator_email && (
              <p
                className="text-[11px] mt-0.5 opacity-90 flex items-center gap-1"
                data-testid="impersonator-role-badge"
              >
                <RoleIcon size={11} />
                <span>via {roleLabel}: {user.impersonator_email}</span>
              </p>
            )}
          </div>
        );
      })()}
      
      <div className="p-6">
        {branding.logo_url ? (
          <img 
            src={branding.logo_url} 
            alt={companyName} 
            className="h-8 object-contain mb-2"
          />
        ) : (
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {companyName}
          </h1>
        )}
        <p className="text-sm text-slate-400 mt-1">{user?.email}</p>
        {user?.tenant_name && (
          <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
            <Building2 size={12} />
            {user.tenant_name}
          </p>
        )}
        
        {/* Tenant Switcher - for users with multi-tenant access */}
        <div className="mt-2">
          <TenantSwitcher />
        </div>
        
        {user?.plan && (
          <span className={`inline-flex items-center mt-2 px-2 py-0.5 rounded text-xs font-medium capitalize ${
            user.plan === 'enterprise' ? 'bg-purple-500/20 text-purple-300' :
            user.plan === 'pro' ? 'bg-indigo-500/20 text-indigo-300' :
            user.plan === 'starter' ? 'bg-green-500/20 text-green-300' :
            'bg-slate-500/20 text-slate-400'
          }`}>
            {user.plan} Plan
          </span>
        )}
        {aiCredits !== null && !isFreemium && (
          <button
            type="button"
            onClick={() => setShowAiPanel(true)}
            className="flex items-center gap-1.5 mt-2 px-2 py-1 bg-amber-500/20 rounded text-xs text-amber-300 hover:bg-amber-500/30 transition-colors w-full"
            data-testid="sidebar-ai-generations-badge"
            title="View & top up your AI Generations"
          >
            <Sparkles size={12} />
            <span>
              {aiCredits.unlimited
                ? 'Unlimited AI Generations'
                : `${aiCredits.total_remaining} AI Generations`}
            </span>
          </button>
        )}
      </div>

      <AiGenerationsPanel open={showAiPanel} onOpenChange={setShowAiPanel} />
      
      <nav className="flex-1 px-4 overflow-y-auto pb-20">
        {/* Top-level items — always visible, never grouped */}
        {topLevelItems.map((item) => {
          const Icon = item.icon;
          const locked = isFreemium && !FREEMIUM_ACCESSIBLE_PATHS.has(item.path);
          const testId = `sidebar-link-${item.name.toLowerCase().replace(/\s+/g, '-')}`;
          if (locked) {
            return (
              <SimpleTooltip key={item.path} content="Upgrade your plan to unlock this feature" side="right">
                <Link to="/settings" data-testid={`${testId}-locked`}
                  className="flex items-center justify-between gap-3 px-4 py-3 rounded-lg mb-1 transition-all text-slate-500 hover:bg-slate-800/40 hover:text-slate-400 opacity-50 cursor-pointer"
                  aria-disabled="true">
                  <span className="flex items-center gap-3"><Icon size={20} /><span>{item.name}</span></span>
                  <Lock size={14} className="opacity-70" />
                </Link>
              </SimpleTooltip>
            );
          }
          return (
            <SimpleTooltip key={item.path} content={item.tooltip} side="right">
              <Link to={item.path} data-testid={testId}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-all ${isActive(item.path) ? 'text-white' : 'text-slate-300 hover:bg-slate-800 hover:text-white'}`}
                style={isActive(item.path) ? { backgroundColor: primaryColor } : {}}>
                <Icon size={20} /><span>{item.name}</span>
              </Link>
            </SimpleTooltip>
          );
        })}

        {/* Grouped menu sections */}
        {menuGroups.map((group) => {
          const isExpanded = expandedGroups.has(group.label);
          const hasActiveItem = group.items.some(i => isActive(i.path));
          return (
            <div key={group.label} className="mb-1">
              {/* Group header */}
              <button
                onClick={() => toggleGroup(group.label)}
                data-testid={`sidebar-group-${group.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`w-full flex items-center justify-between px-4 py-2.5 rounded-lg transition-all text-left ${
                  hasActiveItem
                    ? 'text-white font-medium'
                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
                }`}
                style={hasActiveItem && !isExpanded ? { backgroundColor: `${primaryColor}33` } : {}}
              >
                <span className="text-xs font-semibold uppercase tracking-wider">{group.label}</span>
                {isExpanded
                  ? <ChevronDown size={14} className="opacity-60" />
                  : <ChevronRight size={14} className="opacity-60" />}
              </button>

              {/* Group items */}
              {isExpanded && (
                <div className="ml-2 border-l border-slate-700 pl-2">
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    const locked = isFreemium && !FREEMIUM_ACCESSIBLE_PATHS.has(item.path);
                    const testId = `sidebar-link-${item.name.toLowerCase().replace(/\s+/g, '-')}`;
                    if (locked) {
                      return (
                        <SimpleTooltip key={item.path} content="Upgrade your plan to unlock this feature" side="right">
                          <Link to="/settings" data-testid={`${testId}-locked`}
                            className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg mb-0.5 transition-all text-slate-500 hover:bg-slate-800/40 hover:text-slate-400 opacity-50 cursor-pointer"
                            aria-disabled="true">
                            <span className="flex items-center gap-3"><Icon size={18} /><span className="text-sm">{item.name}</span></span>
                            <Lock size={13} className="opacity-70" />
                          </Link>
                        </SimpleTooltip>
                      );
                    }
                    return (
                      <SimpleTooltip key={item.path} content={item.tooltip} side="right">
                        <Link to={item.path} data-testid={testId}
                          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg mb-0.5 transition-all ${isActive(item.path) ? 'text-white' : 'text-slate-300 hover:bg-slate-800 hover:text-white'}`}
                          style={isActive(item.path) ? { backgroundColor: primaryColor } : {}}>
                          <Icon size={18} /><span className="text-sm">{item.name}</span>
                        </Link>
                      </SimpleTooltip>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {/* Admin Section — on Freemium, only items in FREEMIUM_ACCESSIBLE_ADMIN_PATHS
            are shown (others get the same grey "Upgrade" lock treatment as main items). */}
        {adminMenuItems.length > 0 && (() => {
          const visibleAdminItems = isFreemium
            ? adminMenuItems.filter((it) => FREEMIUM_ACCESSIBLE_ADMIN_PATHS.has(it.path))
            : adminMenuItems;
          if (visibleAdminItems.length === 0) return null;
          return (
          <>
            <div className="mt-4 mb-2 px-4 flex items-center gap-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-red-400">Admin</span>
              <div className="flex-1 h-px bg-red-800/50"></div>
            </div>
            {visibleAdminItems.map((item, index) => {
              const Icon = item.icon;
              return (
                <SimpleTooltip key={`admin-${item.path}-${index}`} content={item.tooltip} side="right">
                  <Link
                    to={item.path}
                    data-testid={`sidebar-link-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-all ${
                      isActive(item.path)
                        ? 'text-white'
                        : `${getLinkColor(item, false)} ${getHoverBg(item)}`
                    }`}
                    style={isActive(item.path) ? { backgroundColor: '#dc2626' } : {}}
                  >
                    <Icon size={20} />
                    <span>{item.name}</span>
                  </Link>
                </SimpleTooltip>
              );
            })}
          </>
          );
        })()}

        {/* Super Admin Section */}
        {superAdminMenuItems.length > 0 && (
          <>
            <div className="mt-4 mb-2 px-4 flex items-center gap-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-amber-400">Super Admin</span>
              <div className="flex-1 h-px bg-amber-800/50"></div>
            </div>
            {superAdminMenuItems.map((item, index) => {
              const Icon = item.icon;
              return (
                <SimpleTooltip key={`super-${item.path}-${index}`} content={item.tooltip} side="right">
                  <Link
                    to={item.path}
                    data-testid={`sidebar-link-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-all ${
                      isActive(item.path)
                        ? 'text-white'
                        : `${getLinkColor(item, false)} ${getHoverBg(item)}`
                    }`}
                    style={isActive(item.path) ? { backgroundColor: '#d97706' } : {}}
                  >
                    <Icon size={20} />
                    <span>{item.name}</span>
                  </Link>
                </SimpleTooltip>
              );
            })}
          </>
        )}

        {masterAdminMenuItems.length > 0 && (
          <>
            <div className="mt-4 mb-2 px-4 flex items-center gap-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-rose-400">Master Admin</span>
              <div className="flex-1 h-px bg-rose-800/50"></div>
            </div>
            {masterAdminMenuItems.map((item, index) => {
              const Icon = item.icon;
              return (
                <SimpleTooltip key={`master-${item.path}-${index}`} content={item.tooltip} side="right">
                  <Link
                    to={item.path}
                    data-testid={`sidebar-link-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-all ${
                      isActive(item.path)
                        ? 'text-white'
                        : `${getLinkColor(item, false)} ${getHoverBg(item)}`
                    }`}
                    style={isActive(item.path) ? { backgroundColor: '#e11d48' } : {}}
                  >
                    <Icon size={20} />
                    <span>{item.name}</span>
                  </Link>
                </SimpleTooltip>
              );
            })}
          </>
        )}
      </nav>

      <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-800 bg-slate-900">
        <button
          onClick={logout}
          data-testid="logout-button"
          className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-slate-800 hover:text-white transition-all w-full"
        >
          <LogOut size={20} />
          <span>Logout</span>
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
